import React from 'react';

import { ICurrentUserDTO, loginCheck } from '@apps/Account/@services/authService';
import { IUser } from '@apps/Users/@services/users.service';
import { success } from '@helpers/notificationHelper';

const mapCurrentUserDTOToUser = (data: ICurrentUserDTO): IUser => {
  return {
    id: +data.id,
    name: data.name,
    surname: data.surname,
    photo: data.photo_50,
  };
};

export interface IAuthLoginCallbackPayload {
  id: number;
  hash: string;
}
export interface IAuthContext {
  isAuth: boolean;
  user: null | IUser;
  loading: boolean;
  onLogin?: (data: IAuthLoginCallbackPayload, cb?: (d: any) => void) => void;
  onLogout?: (cb: () => void) => void;
  updateUser: (user: Partial<Omit<IUser, 'id'>>) => void;
  hash: string;
}

const defaultAuthContext: IAuthContext = {
  isAuth: false,
  loading: true,
  user: null,
  hash: '',
  updateUser: () => {},
};

export const AuthContext = React.createContext<IAuthContext>(defaultAuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = React.useState(defaultAuthContext.loading);
  const [isAuthenticated, setIsAuthenticated] = React.useState(defaultAuthContext.isAuth);
  const [hash, setHash] = React.useState('');
  const [user, setUser] = React.useState(defaultAuthContext.user);

  React.useEffect(() => {
    const authStart = async () => {
      try {
        setIsLoading(true);
        const authCheckResult = await loginCheck();

        if (authCheckResult.result && authCheckResult.user) {
          setIsAuthenticated(true);
          setUser(mapCurrentUserDTOToUser(authCheckResult.user));
          setHash(authCheckResult.hash);

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          window.user = user;
        }
      } catch (e) {
        console.error('AUTH ERROR', e);
      } finally {
        setIsLoading(false);
      }
    };

    authStart();
  }, []);

  const loginHandler = async (data: IAuthLoginCallbackPayload, cb?: (d: any) => void) => {
    success('Привет');

    localStorage.setItem('token', data.hash);

    const loginCheckResult = await loginCheck();

    if (loginCheckResult.result && loginCheckResult.user) {
      success(`Давно не виделись, ${loginCheckResult.user?.name ?? 'дружище'}`);
      setIsAuthenticated(true);
      const userFromCheck = mapCurrentUserDTOToUser(loginCheckResult.user);
      setUser(userFromCheck);
      setHash(data.hash);

      cb?.({
        isAuth: true,
        user: userFromCheck,
      });

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      window.user = { ...data, ...userFromCheck };
    }
  };

  const logoutHandler = (cb: () => void) => {
    if (!user) return;
    localStorage.removeItem('token');

    setIsAuthenticated(false);
    setUser(null);
    setHash('');

    cb();

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    window.user = null;
    success(`До новых походов, ${user.name}`);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuth: isAuthenticated,
        user,
        hash,
        loading: isLoading,
        onLogin: loginHandler,
        onLogout: logoutHandler,
        updateUser: (u) => setUser((c) => ({ ...c, ...u }) as IUser),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function withAuth<P = {}>(Component: React.FC<P & { auth: IAuthContext }>) {
  return function WrapperComponent(props: P & { auth: IAuthContext }) {
    return (
      <AuthContext.Consumer>
        {(state) => <Component {...props} auth={state} />}
      </AuthContext.Consumer>
    );
  };
}

export const AuthConsumer = AuthContext.Consumer;

export default {
  AuthProvider,
  AuthConsumer,
  AuthContext,
  withAuth,
};
